import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { storage, ensureAuthTokenReady } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * Generate PDF for a bill (matches BillView.jsx format exactly)
 * @param {Object} bill - Bill data
 * @returns {jsPDF} - PDF document
 */
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const generateBillPDF = (bill) => {
  if (!bill || typeof bill !== 'object') {
    throw new Error('Invalid bill data for PDF');
  }

  const salonName = (bill.salonName && String(bill.salonName).trim()) || 'Salon';
  const services = Array.isArray(bill.services) ? bill.services : [];

  // Initialize jsPDF - A4 size, portrait orientation
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let yPos = margin;

  // Helper function to add text with automatic line wrapping
  const addWrappedText = (text, x, y, maxWidth, fontSize = 10, align = 'left') => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    
    if (align === 'center') {
      lines.forEach((line, index) => {
        const textWidth = doc.getTextWidth(line);
        doc.text(line, x - (textWidth / 2), y + (index * fontSize * 0.4));
      });
    } else if (align === 'right') {
      lines.forEach((line, index) => {
        doc.text(line, x, y + (index * fontSize * 0.4), { align: 'right' });
      });
    } else {
      doc.text(lines, x, y);
    }
    
    return y + (lines.length * fontSize * 0.4) + 3;
  };

  // Header - Salon Information
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(44, 62, 80); // #2c3e50
  yPos = addWrappedText(salonName.toUpperCase(), pageWidth / 2, yPos, pageWidth - 2 * margin, 20, 'center');
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(85, 85, 85);
  
  if (bill.salonPhone) {
    yPos = addWrappedText(`Phone: ${bill.salonPhone}`, pageWidth / 2, yPos, pageWidth - 2 * margin, 11, 'center');
  }
  
  if (bill.salonAddress) {
    yPos = addWrappedText(bill.salonAddress, pageWidth / 2, yPos, pageWidth - 2 * margin, 10, 'center');
  }
  
  yPos += 5;

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Bill Information
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(44, 62, 80);
  doc.text('Bill No:', margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(String(bill.billNumber ?? '—'), margin + 25, yPos);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Date:', pageWidth / 2, yPos);
  doc.setFont('helvetica', 'normal');
  const billDate = new Date(bill.billDate).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  doc.text(billDate, pageWidth / 2 + 15, yPos);
  
  yPos += 8;

  if (bill.bookingReference) {
    doc.setFont('helvetica', 'bold');
    doc.text('Booking Ref:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(bill.bookingReference, margin + 30, yPos);
    yPos += 8;
  }

  // Divider
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Customer Details
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('CUSTOMER DETAILS', margin, yPos);
  yPos += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(`Name: ${bill.customerName}`, margin, yPos);
  yPos += 5;
  
  if (bill.customerPhone && bill.customerPhone !== 'Not Provided') {
    doc.text(`Phone: ${bill.customerPhone}`, margin, yPos);
    yPos += 5;
  }
  
  yPos += 3;

  // Divider
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Services Table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('SERVICES', margin, yPos);
  yPos += 5;

  // Prepare table data
  const tableData = services.map((service, index) => [
    (index + 1).toString(),
    service.name || 'Service',
    `Rs. ${safeNum(service.price).toFixed(2)}`
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['#', 'Service', 'Price']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [59, 130, 246], // #3B82F6
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 11,
      halign: 'left'
    },
    bodyStyles: {
      fontSize: 10,
      textColor: [68, 68, 68]
    },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      1: { cellWidth: 'auto', halign: 'left' },
      2: { cellWidth: 35, halign: 'right' }
    },
    margin: { left: margin, right: margin },
    alternateRowStyles: {
      fillColor: [245, 247, 250]
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Divider
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Bill Summary
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('BILL SUMMARY', margin, yPos);
  yPos += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  
  // Subtotal
  doc.text('Subtotal:', margin, yPos);
  doc.text(`Rs. ${safeNum(bill.subtotal).toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
  yPos += 6;

  // Discounts
  if (bill.discounts && bill.discounts.length > 0) {
    doc.setTextColor(39, 174, 96); // Green color for discounts
    bill.discounts.forEach((discount) => {
      doc.text(`${discount.description || 'Discount'}:`, margin, yPos);
      doc.text(`-Rs. ${safeNum(discount.amount).toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 6;
    });
    doc.setTextColor(44, 62, 80); // Reset color
  }

  // GST
  if (bill.gstEnabled) {
    doc.text(`GST (${safeNum(bill.gstPercentage)}%):`, margin, yPos);
    doc.text(`Rs. ${safeNum(bill.gstAmount).toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;
  }

  // Thin divider before grand total
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 7;

  // Grand Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('GRAND TOTAL:', margin, yPos);
  doc.text(`Rs. ${safeNum(bill.grandTotal).toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
  yPos += 10;

  // Divider
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Payment Information
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('PAYMENT INFORMATION', margin, yPos);
  yPos += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text('Payment Mode:', margin, yPos);
  doc.text(String(bill.paymentMode || 'cash').toUpperCase(), pageWidth - margin, yPos, { align: 'right' });
  yPos += 6;

  doc.text('Status:', margin, yPos);
  doc.setTextColor(21, 87, 36); // Green for paid status
  doc.setFont('helvetica', 'bold');
  doc.text(String(bill.billStatus || 'PAID'), pageWidth - margin, yPos, { align: 'right' });
  doc.setTextColor(44, 62, 80); // Reset color
  yPos += 10;

  // Divider
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Footer - Thank you message
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(44, 62, 80);
  yPos = addWrappedText(`Thank you for visiting ${salonName}!`, pageWidth / 2, yPos, pageWidth - 2 * margin, 12, 'center');
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(102, 102, 102);
  addWrappedText('We look forward to serving you again!', pageWidth / 2, yPos, pageWidth - 2 * margin, 10, 'center');

  return doc;
};

/**
 * Generate PDF and upload to Firebase Storage
 * @param {Object} bill - Bill data with id
 * @returns {Promise<Object>} - { pdfUrl, pdfFileName }
 */
export const generateAndUploadBillPDF = async (bill) => {
  try {
    await ensureAuthTokenReady();

    // Generate PDF
    const doc = generateBillPDF(bill);
    const rawNum = bill.billNumber != null && bill.billNumber !== '' ? String(bill.billNumber) : bill.id || 'bill';
    const safeFileBase = rawNum.replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileName = `Bill_${safeFileBase}.pdf`;
    
    // Convert to blob
    const pdfBlob = doc.output('blob');
    
    // Upload to Firebase Storage
    const salonId = bill.salonId || 'default-salon';
    const storageFileName = `bills/${salonId}/${fileName}`;
    const storageRef = ref(storage, storageFileName);
    
    console.log('Uploading PDF to Firebase Storage...');
    await uploadBytes(storageRef, pdfBlob);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return {
      pdfUrl: downloadURL,
      pdfFileName: fileName
    };
  } catch (error) {
    throw error;
  }
};

